// The Control PIN step-up any action can await (UC-IDENTITY-008 step 3; TEN-57). `withStepUp(run)` calls the action; on
// 403 STEP_UP_REQUIRED it opens the modal with the action, record and authoriser roles from `meta`, posts /auth/step-up and
// repeats the action with X-StepUp-Token. Refused keeps the modal open with one generic sentence; Locked shows lockedUntil; the
// right one-time PIN of an in-person reset says it has to be replaced first (UC-IDENTITY-008 E8).
// Enter authorises; the PIN auto-submits only when the tenant's minimum and maximum PIN lengths are equal (owner, 15 Sep 2026).
// The modal is drawn above every page, so a screen that may go while it is open passes a signal: aborted, the prompt is withdrawn,
// answered as cancelled as on a lock, and none opens after (`useStepUpWhileMounted`; UC-CATALOGUE-003 A12 step 2). Without one the
// prompt stays until it is answered, as UC-IDENTITY-008 specifies (its open questions: the screens whose prompt outlives their page).
import { ROLES, type StepUpAction } from "@visionx/contracts";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { auth } from "../api/endpoints";
import { describeError, isApiError } from "../api/problem";
import { formatTime } from "../lib/format";
import { useSession, useSessionView } from "../session/store";
import { useFocusTrap } from "../ui/focus-trap";
import { Icon } from "../ui/icons";

export interface StepUpGrant { token?: string | undefined; reason?: string | undefined }

export interface StepUpOptions {
  /** Modal title, e.g. "Deactivate branch". */
  title: string;
  /** One sentence describing what is being authorised. */
  description?: string | undefined;
  /** Whether the modal collects a reason for the Control Log. Default `optional`. */
  reason?: "required" | "optional" | "none" | undefined;
  /**
   * Aborted, the screen that asked is gone: its prompt is withdrawn — answered as cancelled, as on a lock, so nothing is sent and
   * nothing said — and no prompt opens for it after (the closing check of the third fix round, 1 Nov 2026, C3V2-1). Without one a
   * prompt stays until it is answered.
   */
  signal?: AbortSignal | undefined;
}

interface Required { action: StepUpAction; recordType: string; recordId: string | null; authoriserRoles: string[] }

export type StepUpRunner = <T>(run: (grant: StepUpGrant) => Promise<T>, opts: StepUpOptions) => Promise<T | undefined>;

interface Pending { nonce: number; required: Required; opts: StepUpOptions; note: string | null; resolve: (g: StepUpGrant | null) => void }

const Ctx = createContext<StepUpRunner | null>(null);

export function useStepUp(): StepUpRunner {
  const v = useContext(Ctx);
  if (!v) throw new Error("useStepUp needs a StepUpProvider");
  return v;
}

/**
 * `withStepUp` bound to the component that calls it: a prompt it asks is withdrawn when that component unmounts — a dialog of the
 * product's panel closed by another product opened, or by the page left, under the Control PIN dialog — answered as cancelled, so
 * nothing is sent and nothing said (UC-CATALOGUE-003 A12 step 2; C3V2-1). The signal is made in an effect and aborted in its cleanup,
 * so React's StrictMode, which mounts a component twice in development, withdraws nothing: the second mount makes a signal of its own.
 */
export function useStepUpWhileMounted(): StepUpRunner {
  const withStepUp = useStepUp();
  const mounted = useRef<AbortController | null>(null);
  useEffect(() => {
    const own = new AbortController();
    mounted.current = own;
    return () => own.abort();
  }, []);
  return useCallback<StepUpRunner>((run, opts) => withStepUp(run, { ...opts, signal: mounted.current?.signal }), [withStepUp]);
}

function requiredFrom(meta: Record<string, unknown>): Required {
  const roles = Array.isArray(meta.authoriserRoles) ? meta.authoriserRoles.map(String) : [];
  return { action: String(meta.action ?? "Other") as StepUpAction, recordType: String(meta.recordType ?? "record"), recordId: meta.recordId ? String(meta.recordId) : null, authoriserRoles: roles };
}

const MAX_PROMPTS = 3;
let nonce = 0;

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const session = useSession();
  // A lock or a session end while a prompt is open cancels it: the screen that asked is gone behind the lock screen and a PIN
  // typed now would be refused (adversarial review 16 Sep 2026, L9).
  useEffect(() => { if (pending && session.status !== "active") pending.resolve(null); }, [pending, session.status]);

  const withStepUp = useCallback<StepUpRunner>(async (run, opts) => {
    const { signal } = opts;
    let grant: StepUpGrant = {};
    let note: string | null = null;
    for (let attempt = 0; attempt <= MAX_PROMPTS; attempt++) {
      try {
        return await run(grant);
      } catch (err) {
        if (!(isApiError(err) && err.code === "STEP_UP_REQUIRED") || attempt === MAX_PROMPTS) throw err;
        // The screen that asked is gone (`signal`): no prompt opens for it, and one open is withdrawn, answered as on a lock.
        if (signal?.aborted) return undefined;
        const required = requiredFrom(err.meta);
        const answer = await new Promise<StepUpGrant | null>((resolve) => {
          const withdraw = () => resolve(null);
          signal?.addEventListener("abort", withdraw, { once: true });
          setPending({ nonce: ++nonce, required, opts, note, resolve: (g) => { signal?.removeEventListener("abort", withdraw); resolve(g); } });
        });
        setPending(null);
        if (!answer) return undefined;
        grant = answer;
        note = "The authorisation expired or was already used. Enter the PIN again.";
      }
    }
    return undefined;
  }, []);

  return (
    <Ctx.Provider value={withStepUp}>
      {children}
      {pending && <StepUpModal key={pending.nonce} pending={pending} />}
    </Ctx.Provider>
  );
}

const ROLE_LABEL: Record<string, string> = { Owner: "Owner", Admin: "Admin", BranchOwner: "Branch owner", Optometrist: "Optometrist", Dispenser: "Dispenser", FrameConsultant: "Frame consultant", Receptionist: "Receptionist" };

function StepUpModal({ pending }: { pending: Pending }) {
  const session = useSessionView();
  const minLength = session?.settings.pinMinLength ?? 4;
  const maxLength = session?.settings.pinMaxLength ?? 6;
  const fixedLength = minLength === maxLength;
  const [controlId, setControlId] = useState(session?.user.controlIdDisplay ?? "");
  const [pin, setPin] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // `focus: "control"` sends the cursor to the Control ID after the answer — another authoriser is the way on.
  const [message, setMessage] = useState<{ tone: "crit" | "warn"; text: string; focus?: "control" } | null>(pending.note ? { tone: "warn", text: pending.note } : null);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const formRef = useRef<HTMLFormElement>(null);
  const controlRef = useRef<HTMLInputElement>(null);
  const pinRef = useRef<HTMLInputElement>(null);
  const submittedRef = useRef(false);
  const { required, opts } = pending;
  useFocusTrap(formRef);
  const reasonMode = opts.reason ?? "optional";
  const locked = lockedUntil !== null && Date.parse(lockedUntil) > now;

  useEffect(() => { controlRef.current?.focus(); controlRef.current?.select(); }, []);
  useEffect(() => {
    if (!lockedUntil) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [lockedUntil]);

  const submit = useCallback(async () => {
    if (busy || submittedRef.current || locked) return;
    const id = Number(controlId);
    if (!Number.isInteger(id) || id < 1 || controlId.trim() === "") { setMessage({ tone: "crit", text: "Enter a Control ID." }); controlRef.current?.focus(); return; }
    if (pin.length < minLength) { setMessage({ tone: "crit", text: fixedLength ? `Enter your ${minLength}-digit Control PIN.` : `Enter your Control PIN (${minLength} to ${maxLength} digits).` }); pinRef.current?.focus(); return; }
    if (reasonMode === "required" && reason.trim() === "") { setMessage({ tone: "crit", text: "A reason is required." }); return; }
    submittedRef.current = true;
    setBusy(true);
    setMessage(null);
    try {
      const body = { controlId: id, credential: pin, action: required.action, recordType: required.recordType, ...(required.recordId ? { recordId: required.recordId } : {}) };
      const { data } = await auth.stepUp(body);
      pending.resolve({ token: data.stepUpToken, ...(reason.trim() ? { reason: reason.trim() } : {}) });
    } catch (err) {
      submittedRef.current = false;
      setPin("");
      if (isApiError(err) && err.code === "STEP_UP_REFUSED" && err.meta.reason === "pin_must_change") {
        // The right one-time PIN of an in-person reset authorises nothing until it is replaced (BR-IDENTITY-035).
        setMessage({ tone: "warn", text: "That one-time PIN can't authorise until it is replaced. Choose a new PIN at your next sign-in, then authorise.", focus: "control" });
      } else if (isApiError(err) && err.code === "STEP_UP_REFUSED") {
        setMessage({ tone: "crit", text: "Not accepted. Try again." });
      } else if (isApiError(err) && err.code === "STEP_UP_LOCKED") {
        const until = typeof err.meta.lockedUntil === "string" ? err.meta.lockedUntil : null;
        setLockedUntil(until);
        setMessage({ tone: "crit", text: until ? `This Control ID is locked until ${formatTime(until)}.` : "This Control ID is locked." });
      } else {
        setMessage({ tone: "crit", text: describeError(err) });
      }
    } finally {
      setBusy(false);
    }
  }, [busy, locked, controlId, pin, minLength, maxLength, fixedLength, reasonMode, reason, required, pending]);

  // Auto-submit only when every PIN has the same length (UC-IDENTITY-008 step 3): the last digit is then the whole PIN.
  // Otherwise Enter or Authorise submits, so a PIN longer than the minimum can be typed in full.
  useEffect(() => {
    if (fixedLength && pin.length === maxLength && !busy && !submittedRef.current && !locked) void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  // After a refusal the PIN field is re-enabled on the next render; focus it then, not before — a disabled input cannot take focus.
  useEffect(() => {
    if (busy || locked || !message) return;
    if (message.focus === "control") { controlRef.current?.focus(); controlRef.current?.select(); }
    else if (message.tone === "crit") pinRef.current?.focus();
  }, [busy, locked, message]);
  // Escape cancels wherever focus is: after a refusal it has moved, and a touch keypad may focus nothing at all.
  useEffect(() => {
    const onEscape = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); pending.resolve(null); } };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [pending]);
  const onForm = (e: FormEvent) => { e.preventDefault(); void submit(); };
  // One box per digit of the minimum, and one more only for a digit typed past it: an empty box after the minimum read as a
  // demand for more digits from someone whose PIN was complete (staging, 7 Oct 2026). Enter or the button submits it.
  const digits = Math.max(minLength, pin.length);
  // The server sends every role when the tenant restricts nobody (BR-IDENTITY-010): that is no restriction to name (UC-IDENTITY-008
  // step 3; testing of 12 Oct 2026, finding 40).
  const unrestricted = ROLES.every((r) => required.authoriserRoles.includes(r));
  const roles = unrestricted ? [] : required.authoriserRoles.map((r) => ROLE_LABEL[r] ?? r);

  return (
    <div className="backdrop">
      <form ref={formRef} className="modal" role="dialog" aria-modal="true" aria-labelledby="pin-title" onSubmit={onForm}>
        <div className="panel-head">
          <Icon name="lock" width={16} height={16} />
          <h2 id="pin-title">{opts.title}</h2>
          <span className="meta right">Writes a Control Log entry</span>
        </div>
        <div className="panel-body">
          <div className="alert info">
            <Icon name="info" />
            <span>
              {opts.description ?? `${opts.title} needs authorisation.`}{" "}
              {roles.length > 0 ? `A supervisor's Control ID and PIN are required (${roles.join(", ")}).` : "Any active user's Control ID and PIN may authorise."} This will be written to the Control Log.
            </span>
          </div>
          {message && (
            <div className={`alert ${message.tone}`} role="alert">
              <Icon name="warning" />
              <span>{message.text}</span>
            </div>
          )}
          <div className="field">
            <label htmlFor="stepup-control-id">Control ID</label>
            <div className="input mono"><input id="stepup-control-id" ref={controlRef} inputMode="numeric" pattern="[0-9]*" autoComplete="off" maxLength={4} value={controlId} disabled={busy || locked} onChange={(e) => setControlId(e.target.value.replace(/\D/g, ""))} /></div>
            <div className="help">Prefilled with your ID; a supervisor may enter theirs</div>
          </div>
          <div className="field">
            <label htmlFor="stepup-pin">Control PIN</label>
            <div className="pinbox" role="group" aria-label={fixedLength ? `Control PIN, ${minLength} digits` : `Control PIN, ${minLength} to ${maxLength} digits`}>
              {Array.from({ length: digits }, (_, i) => (
                <span key={i} className={`digit${i === pin.length ? " cur" : ""}`} aria-hidden="true">{i < pin.length ? "•" : ""}</span>
              ))}
              <input id="stepup-pin" ref={pinRef} type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" maxLength={maxLength} aria-label="Control PIN" value={pin} disabled={busy || locked}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, maxLength))} />
            </div>
            {!fixedLength && <div className="help">{minLength} to {maxLength} digits · Enter authorises</div>}
          </div>
          {reasonMode !== "none" && (
            <div className="field">
              <label htmlFor="stepup-reason">Reason {reasonMode === "required" && <span className="req">*</span>}</label>
              <div className="input"><input id="stepup-reason" value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} maxLength={500} /></div>
            </div>
          )}
          <div className="help">5 wrong attempts within 15 minutes lock this PIN for 15 minutes and notify the owner.</div>
        </div>
        <div className="panel-foot">
          <button className="btn" type="button" onClick={() => pending.resolve(null)}>Cancel</button>
          <button className="btn primary" type="submit" disabled={busy || locked}><Icon name="lock" />Authorise</button>
        </div>
      </form>
    </div>
  );
}
